// The order state machine , single source of truth.
//
// Replaces:
//   - lib/order-status.ts        (7-line isOrderPaid shim)
//   - the private TERMINAL_ORDER_STATUSES sets duplicated across
//     lib/server/operating-modes.ts, lib/server/brands.ts and
//     app/c/dashboard/sections/shared.tsx
//
// This module imports NOTHING so the server, the client and build-time guard
// scripts can all share the same vocabulary. See doc 01 (sessions, orders &
// payments), §5.1, which this file implements. Where this file and the rest of
// the code disagree, this file is the contract and the code must migrate.
//
// Fulfilment status (ORDER_STATUS) and payment status (PAYMENT_STATUS) are fully
// orthogonal. An order can be `completed` and `unpaid` (house account) or
// `cancelled` and `refunded`. The legacy model smeared the two across columns
// (doc 01, defect O3); STATUS-01 retires that.

// ---------------------------------------------------------------------------
// Fulfilment status
// ---------------------------------------------------------------------------

export const ORDER_STATUS = [
  'draft', // building, not submitted
  'placed', // submitted by guest/staff, awaiting acceptance
  'accepted', // merchant accepted
  'preparing', // in the kitchen
  'ready', // ready for pickup/serve
  'delivered', // handed off to the floor/courier (dispatch terminal). Doc 02 SURF-04.
  'served', // delivered to guest (post-delivery honour)
  'delivery_exception', // handoff failed; operator must resolve (doc 02 SURF-04)
  'completed', // fulfilled and closed
  'cancelled', // ended before fulfilment
  'expired', // timed out unsubmitted
  'no_show', // scheduled, guest never arrived
] as const;

export type OrderStatus = (typeof ORDER_STATUS)[number];

export function isOrderStatus(value: unknown): value is OrderStatus {
  return typeof value === 'string' && (ORDER_STATUS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Payment status
// ---------------------------------------------------------------------------

export const PAYMENT_STATUS = [
  'unpaid',
  'awaiting_payment',
  'pending_proof',
  'pending_review',
  'partial',
  'paid',
  'partially_refunded',
  'refunded',
  'void',
  'invoiced',
  'waived',
  'comped',
] as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[number];

export function isPaymentStatus(value: unknown): value is PaymentStatus {
  return typeof value === 'string' && (PAYMENT_STATUS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Terminal fulfilment statuses
// ---------------------------------------------------------------------------

export const TERMINAL_ORDER_STATUS = ['completed', 'cancelled', 'expired', 'no_show'] as const;
export type TerminalOrderStatus = (typeof TERMINAL_ORDER_STATUS)[number];

export function isTerminalOrderStatus(value: unknown): value is TerminalOrderStatus {
  return (
    typeof value === 'string' && (TERMINAL_ORDER_STATUS as readonly string[]).includes(value)
  );
}

/** An order is "over" (no further fulfilment transition possible) when its
 *  fulfilment status is terminal. */
export function isOrderOver(status: OrderStatus | unknown): boolean {
  return isTerminalOrderStatus(status);
}

// ---------------------------------------------------------------------------
// Legal transitions
// ---------------------------------------------------------------------------

export const LEGAL_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  draft: ['placed', 'expired', 'cancelled'],
  placed: ['accepted', 'preparing', 'cancelled', 'expired'],
  // `expired` is reachable from `accepted` because of pickup approval
  // (docs/operational-ux-2026-09/07-customer-ordering.md §3): the counter says
  // yes, which arms a payment window, and an unpaid window closing expires the
  // order. The sweep and the lazy reader both write that transition, so the map
  // that declares what is legal has to know about it.
  //
  // `served` is reachable from `accepted` for the same reason the quick-service
  // ladder exists at all. A fast-food counter has no separate "started cooking"
  // or "ready for pickup" step to press: the food leaves the pass and the guest
  // takes it, so an order that already sits at `accepted` — approved, paid, or
  // simply carried over from a hostess's accept — is handed over in one move.
  // Without this the POS offers the single button the shop actually uses and the
  // server rejects it: "Illegal order transition: accepted -> served", the exact
  // report from BiteBox. It is a widening, not a bypass: the prep states are
  // still optional everywhere, and full service keeps every one of them.
  accepted: ['preparing', 'served', 'cancelled', 'expired'],
  // `cancelled` from `preparing` is a real operation, not an edge case: a
  // kitchen that cannot make the dish stops it, and the walk-in that will not
  // wait. The order is in the kitchen but nothing has been handed over, so
  // cancelling loses no fulfilment and settles nothing. It is separate from
  // `ready -> cancelled`, which discards food already plated.
  preparing: ['ready', 'preparing', 'accepted', 'cancelled'],
  ready: ['served', 'delivered', 'preparing', 'cancelled'],
  delivered: ['delivery_exception', 'completed'],
  served: ['completed'],
  delivery_exception: ['completed'],
  completed: [],
  cancelled: [],
  expired: [],
  no_show: [],
};

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  if (from === to) return true;
  return (LEGAL_TRANSITIONS[from] ?? []).includes(to);
}

/** The status every order is born at. Order creation has no from-state, so it
 *  cannot pass `assertTransition`; it takes its value from here instead of
 *  restating the literal at each insert site (doc 01 R01.1). */
export const INITIAL_ORDER_STATUS: OrderStatus = 'draft';

/**
 * Guarded value for a `service_orders.status` write made OUTSIDE the POS action
 * dispatcher — the creation/fire paths, the stale-draft recovery sweep and the
 * hotel night audit, none of which are POS actions and none of which can route
 * through `pos-workspace-actions.ts`.
 *
 * It asserts every hop of the shortest legal path from `from` to `to` and then
 * returns `to`, so the value that lands in the update payload always originates
 * from this module having passed {@link assertTransition} — never from a bare
 * literal typed at the call site (R01.1 / GUARD-01).
 *
 * Multi-hop on purpose: a sweep closing an order that is still `placed` is
 * making the same claim as five separate transitions, and each one must still be
 * legal. Throws {@link IllegalOrderTransitionError} when no legal path exists,
 * which callers map to a 409 rather than a 500 (doc 01 R5.3).
 */
export function nextOrderStatus(
  from: unknown,
  to: OrderStatus,
  ctx: TransitionContext = {},
): OrderStatus {
  if (!isOrderStatus(from)) {
    throw new IllegalOrderTransitionError(
      from as OrderStatus,
      to,
      `Unknown from-status: ${String(from)}`,
    );
  }
  if (from === to) return to;

  const path = orderStatusPath(from, to);
  if (!path) throw new IllegalOrderTransitionError(from, to);

  let cursor: OrderStatus = from;
  for (const hop of path) {
    // The context (cancellation actor, reason) belongs to the transition the
    // caller actually asked for, not to the intermediate hops it implies.
    assertTransition(cursor, hop, hop === to ? ctx : {});
    cursor = hop;
  }
  return to;
}

/** Who cancelled an order. Stored on the new `cancelled_by` column (doc 01, §5.1),
 *  replacing the legacy three-value cancellation vocabulary
 *  (`cancelled`, `cancelled_by_merchant`, `cancelled_by_customer`). */
export const CANCELLATION_ACTOR = ['guest', 'merchant', 'system'] as const;
export type CancellationActor = (typeof CANCELLATION_ACTOR)[number];

export function isCancellationActor(value: unknown): value is CancellationActor {
  return typeof value === 'string' && (CANCELLATION_ACTOR as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Transition guard
// ---------------------------------------------------------------------------

export interface TransitionContext {
  /** Required when transitioning into `cancelled`. */
  cancellationActor?: CancellationActor;
  /** Free-text reason for the transition (recommended for cancellations/voids). */
  reason?: string;
}

/** Typed error thrown on an illegal fulfilment transition. Callers map this to a
 *  409, never a 500 (doc 01 R5.3). */
export class IllegalOrderTransitionError extends Error {
  constructor(
    public readonly from: OrderStatus,
    public readonly to: OrderStatus,
    message?: string,
  ) {
    super(message ?? `Illegal order transition: ${from} -> ${to}`);
    this.name = 'IllegalOrderTransitionError';
  }
}

/** The single guard every fulfilment-status write must pass through.
 *  `pos-workspace-actions.ts` is the only entry point, so this is one insertion
 *  point, not 21 (doc 01 R01.1). */
export function assertTransition(from: OrderStatus, to: OrderStatus, ctx: TransitionContext = {}): void {
  if (!isOrderStatus(from)) {
    throw new IllegalOrderTransitionError(from as OrderStatus, to, `Unknown from-status: ${String(from)}`);
  }
  if (!isOrderStatus(to)) {
    throw new IllegalOrderTransitionError(from, to as OrderStatus, `Unknown to-status: ${String(to)}`);
  }
  if (!canTransition(from, to)) {
    throw new IllegalOrderTransitionError(from, to);
  }
  if (to === 'cancelled' && !isCancellationActor(ctx.cancellationActor)) {
    throw new IllegalOrderTransitionError(
      from,
      to,
      `Transition into 'cancelled' requires a cancellationActor (guest|merchant|system)`,
    );
  }
}

// ---------------------------------------------------------------------------
// Dispatch projection mapping (doc 26 R26.1 / R26.2)
// ---------------------------------------------------------------------------

/**
 * Doc 26 §4.1: dispatch is a *projection* of the order lifecycle, never a
 * parallel writer. This table is the ONLY way a delivery transition may drive
 * `service_orders.status`, and it is the explicit R26.1 mapping:
 *
 *   assigned  -> none       (assignment is a logistical fact, not a fulfilment)
 *   picked_up -> served     (the food has left the kitchen)
 *   en_route  -> none
 *   delivered -> completed  (a courier delivery *is* the completion event)
 *   failed    -> none       (the order stays non-terminal; `delivery_exception`
 *                            is the operator-resolvable signal, never a silent
 *                            terminal state — R26.1)
 *   pending   -> none
 */
export const DISPATCH_TO_ORDER_STATUS: Record<string, OrderStatus | null> = {
  assigned: null,
  // Doc 66 §1.3 — the arrival taps. Both are pure courier-position facts: the food
  // has not changed hands at `at_pickup` and the order is not complete at `arrived`,
  // so neither may move the ORDER's lifecycle. They exist because "the rider is at
  // the restaurant" and "the rider is outside" are the two moments the kitchen and
  // the customer actually care about, and until now nothing could express either.
  at_pickup: null,
  picked_up: 'served',
  en_route: null,
  arrived: null,
  delivered: 'completed',
  failed: null,
  pending: null,
};

/**
 * Shortest legal path of fulfilment statuses from `from` to `to`, excluding
 * `from` and including `to`. Returns `[]` for a no-op and `null` when `to` is
 * unreachable from `from` under {@link LEGAL_TRANSITIONS}. Used to drive the
 * order lifecycle from a dispatch transition leg-by-leg so an illegal
 * intermediate is never written.
 */
export function orderStatusPath(from: OrderStatus, to: OrderStatus): OrderStatus[] | null {
  if (from === to) return [];
  const queue: Array<{ node: OrderStatus; path: OrderStatus[] }> = [{ node: from, path: [] }];
  const seen = new Set<OrderStatus>([from]);
  while (queue.length > 0) {
    const { node, path } = queue.shift()!;
    for (const next of LEGAL_TRANSITIONS[node]) {
      if (seen.has(next)) continue;
      const nextPath = [...path, next];
      if (next === to) return nextPath;
      seen.add(next);
      queue.push({ node: next, path: nextPath });
    }
  }
  return null;
}

/**
 * Resolve the order-fulfilment status a delivery transition implies, validating
 * that a legal path exists from `current` to it through the lifecycle guard.
 * Returns the status to write, or `null` when the dispatch transition must not
 * move the order.
 *
 * R26.2 — dispatch calls this (and therefore `assertTransition`) every time
 * rather than writing `service_orders.status` directly, so a delivery state can
 * never silently reach an illegal/terminal order state.
 */
export function resolveDispatchOrderStatus(
  currentOrderStatus: string,
  deliveryNext: string,
): OrderStatus | null {
  if (!isOrderStatus(currentOrderStatus)) return null;
  const target = DISPATCH_TO_ORDER_STATUS[deliveryNext];
  if (!target || target === currentOrderStatus) return null;

  const path = orderStatusPath(currentOrderStatus, target);
  if (!path) {
    throw new IllegalOrderTransitionError(
      currentOrderStatus,
      target,
      `Dispatch '${deliveryNext}' implies order status '${target}', but no legal lifecycle path exists from '${currentOrderStatus}'.`,
    );
  }
  // Assert each hop so an illegal intermediate can never be written.
  let cursor = currentOrderStatus;
  for (const hop of path) {
    assertTransition(cursor, hop);
    cursor = hop;
  }
  return target;
}

// ---------------------------------------------------------------------------
// Legacy status migration map (STATUS-01)
// ---------------------------------------------------------------------------

export interface LegacyStatusMapping {
  status: OrderStatus;
  payment_status: PaymentStatus;
  /** Set when the legacy status was a cancellation variant. */
  cancelled_by?: CancellationActor;
  /** True when fulfilment cannot be derived 1:1 and must come from timestamps
   *  (fired_at / ready_at / settled_at) instead. */
  needsTimestampDerivation?: boolean;
}

/** Maps every legacy `service_orders.status` value forward to exactly one V3
 *  pair. Read from the actual write sites in doc 01 §5.3. Nothing is guessed;
 *  unmappable rows are reported by the migration rather than defaulted. */
export const LEGACY_ORDER_STATUS_MAP: Record<string, LegacyStatusMapping> = {
  // fulfilment-only, 1:1
  draft: { status: 'draft', payment_status: 'unpaid' },
  open: { status: 'placed', payment_status: 'unpaid' },
  pending: { status: 'placed', payment_status: 'unpaid' },
  queued: { status: 'placed', payment_status: 'unpaid' },
  placed: { status: 'placed', payment_status: 'unpaid' },
  confirmed: { status: 'accepted', payment_status: 'unpaid' },
  verified: { status: 'accepted', payment_status: 'unpaid' },
  not_fired: { status: 'accepted', payment_status: 'unpaid' },
  preparing: { status: 'preparing', payment_status: 'unpaid' },
  ready: { status: 'ready', payment_status: 'unpaid' },
  done: { status: 'completed', payment_status: 'unpaid' },
  fulfilled: { status: 'completed', payment_status: 'unpaid' },
  closed: { status: 'completed', payment_status: 'unpaid' },
  cancelled: { status: 'cancelled', payment_status: 'unpaid', cancelled_by: 'system' },
  cancelled_by_merchant: { status: 'cancelled', payment_status: 'unpaid', cancelled_by: 'merchant' },
  cancelled_by_customer: { status: 'cancelled', payment_status: 'unpaid', cancelled_by: 'guest' },
  expired: { status: 'expired', payment_status: 'unpaid' },
  no_show: { status: 'no_show', payment_status: 'unpaid' },
  // payment-valued legacy statuses , fulfilment derived from timestamps
  unpaid: { status: 'placed', payment_status: 'unpaid', needsTimestampDerivation: true },
  awaiting_payment: {
    status: 'placed',
    payment_status: 'awaiting_payment',
    needsTimestampDerivation: true,
  },
  pending_proof: {
    status: 'placed',
    payment_status: 'pending_proof',
    needsTimestampDerivation: true,
  },
};

export function mapLegacyOrderStatus(legacy: string): LegacyStatusMapping | null {
  if (isOrderStatus(legacy)) {
    // Already a V3 fulfilment status , pass through unchanged.
    return { status: legacy, payment_status: 'unpaid' };
  }
  return LEGACY_ORDER_STATUS_MAP[legacy] ?? null;
}

// ---------------------------------------------------------------------------
// "Is anything still live?" — for gates that must not close while work is open
// ---------------------------------------------------------------------------

/**
 * Payment statuses that mean the money is settled, so an order carrying one
 * cannot be outstanding work regardless of its fulfilment status.
 *
 * `invoiced` is deliberately absent: an invoiced order is still owed and is
 * still the merchant's problem until it is paid. `partial` and
 * `partially_refunded` are absent for the same reason.
 */
const SETTLED_PAYMENT_STATUS: readonly string[] = [
  'paid',
  'refunded',
  'partially_refunded',
  'waived',
  'comped',
  'void',
];

/** Has the money on this order moved, either direction? `invoiced`, `partial`
 *  and `awaiting_payment` are still owed, so they are not settled. */
export function isSettledPaymentStatus(status: unknown): boolean {
  return typeof status === 'string' && SETTLED_PAYMENT_STATUS.includes(status);
}

/**
 * Every `service_orders.status` value that means "this order is finished", for
 * the purpose of a gate that refuses to act while live records exist.
 *
 * This is NOT the same list as `TERMINAL_ORDER_STATUS`, and the difference is
 * the whole point. `TERMINAL_ORDER_STATUS` answers a purely canonical question:
 * "which V3 fulfilment statuses are terminal?" A gate has to answer it against
 * the RAW column, which still holds legacy values (doc 01 defect O3), so three
 * groups belong here:
 *
 *  1. the canonical terminal statuses;
 *  2. legacy spellings that RESOLVE to a terminal status — `done`, `fulfilled`
 *     and `closed` all map to `completed` (see `LEGACY_ORDER_STATUS_MAP`);
 *  3. payment-valued statuses left in the fulfilment column by the O3 defect.
 *     An order whose status literally reads `paid` is not outstanding work, and
 *     a gate that counted it as live would never be satisfiable.
 *
 * Derived from `TERMINAL_ORDER_STATUS` and `LEGACY_ORDER_STATUS_MAP` rather than
 * hand-listed, because the hand-listed copies are exactly what drifted: three
 * private sets each claimed `ready` and `served` were terminal (they are not —
 * both still advance to `completed`) while omitting `completed` entirely, so a
 * brand could be retired with a `ready` order on the counter and, conversely,
 * never retired at all because one order had reached `completed`.
 */
export const NON_LIVE_ORDER_STATUS: readonly string[] = (() => {
  const out = new Set<string>(TERMINAL_ORDER_STATUS);

  // (2) legacy spellings whose fulfilment resolves to terminal.
  for (const [legacy, mapping] of Object.entries(LEGACY_ORDER_STATUS_MAP)) {
    if (isTerminalOrderStatus(mapping.status)) out.add(legacy);
  }

  // (3) payment-valued statuses sitting in the fulfilment column.
  for (const status of SETTLED_PAYMENT_STATUS) out.add(status);

  return Array.from(out).sort();
})();

/**
 * Is this order still live — i.e. does it represent work or money outstanding?
 *
 * The positive form of `NON_LIVE_ORDER_STATUS`, for callers holding one status
 * rather than building a database filter. Accepts canonical statuses, legacy
 * spellings and payment values, because the column can hold any of the three.
 */
export function isLiveOrderStatus(status: unknown): boolean {
  if (typeof status !== 'string') return false;
  if (NON_LIVE_ORDER_STATUS.includes(status)) return false;
  // An unmapped value is LIVE. A gate that let an unknown status through would
  // let an order the code does not understand block a retirement forever, and
  // the audit trail for that is a support ticket, not a report.
  return true;
}

/**
 * The `status.not.in.(...)` filter these gates send to PostgREST.
 *
 * Exists so the wire format is built in one place. `brands.ts` and
 * `operating-modes.ts` both used to hand-assemble this string from a private
 * set; a typo there is not a compile error, it is a silently wrong query.
 */
export function nonLiveOrderStatusFilter(): string {
  return `(${NON_LIVE_ORDER_STATUS.join(',')})`;
}

/**
 * Statuses past which a CUSTOMER has nothing left to track.
 *
 * Distinct from both sets above, and the distinction is load-bearing:
 *  - `NON_LIVE_ORDER_STATUS` asks "is there work or money outstanding?" — a
 *    gate question. `ready` and `served` are live by that measure, because both
 *    still advance to `completed`.
 *  - this set asks "is there anything left to show the guest?" — a UI
 *    question. `served` is finished for the guest because they are holding the
 *    food, even though the fulfilment state machine has not called it.
 *
 * `delivered` is deliberately absent: it can still move to
 * `delivery_exception`, so tracking stays available.
 */
export const ORDER_TRACKING_CLOSED_STATUS: readonly string[] = [
  ...TERMINAL_ORDER_STATUS,
  'served',
];

export function isOrderTrackingClosed(status: unknown): boolean {
  return (
    typeof status === 'string' &&
    (ORDER_TRACKING_CLOSED_STATUS as readonly string[]).includes(status)
  );
}

/**
 * Is there nothing left to show the guest about this order?
 *
 * Broader than `isOrderTrackingClosed` by one clause, and the extra clause is
 * the whole difference between the two Chow screens and the customer dashboard:
 * an order that is still being tracked can still be financially finished, and
 * for a "reorder" list that is enough to retire it.
 *
 * Use `isOrderTrackingClosed` when the question is strictly "is there a status
 * change left to follow?" and this one when it is "should this order still be
 * surfaced to the customer at all?".
 */
export function isOrderClosedToGuest(status: unknown): boolean {
  return isOrderTrackingClosed(status) || isSettledPaymentStatus(status);
}

/** Positive form of `isOrderClosedToGuest`: should this order still be surfaced
 *  to the customer at all? An absent status is not a live order. */
export function isOrderLiveForGuest(status: unknown): boolean {
  return typeof status === 'string' && status.length > 0 && !isOrderClosedToGuest(status);
}

// ---------------------------------------------------------------------------
// Shift close — the hand-off boundary
// ---------------------------------------------------------------------------

/**
 * Statuses where the order has physically reached the guest.
 *
 * `delivered` (doc 02 SURF-04, handed to the floor or courier) was missing
 * from the shift-close protected list, whose own comment describes it as
 * "food has been delivered and money is genuinely owed". An order sitting in
 * `delivered` was therefore neither auto-cancelled nor reported as protected,
 * and — because the loader only reads the union of the two lists — did not
 * appear in the shift-close summary at all.
 */
export const GUEST_REACHED_STATUS: readonly string[] = ['delivered', 'served'];

/**
 * Statuses a shift close may auto-cancel without an explicit override: still
 * in the kitchen, nothing handed over.
 *
 * `awaiting_prepayment` and `awaiting_payment` are payment values that defect
 * O3 left in the fulfilment column; they are still raw `status` values the
 * queries must match, so they stay listed here and in
 * `SHIFT_CLOSE_PROTECTED_STATUS` rather than being quietly dropped.
 */
export const SHIFT_CLOSE_AUTO_CANCEL_STATUS: readonly string[] = [
  'placed',
  'accepted',
  'preparing',
  'ready',
  'awaiting_prepayment',
];

export const SHIFT_CLOSE_PROTECTED_STATUS: readonly string[] = [
  ...GUEST_REACHED_STATUS,
  'awaiting_payment',
];

export function isShiftCloseAutoCancelable(status: unknown): boolean {
  return (
    typeof status === 'string' &&
    (SHIFT_CLOSE_AUTO_CANCEL_STATUS as readonly string[]).includes(status)
  );
}

export function isShiftCloseProtected(status: unknown): boolean {
  return (
    typeof status === 'string' &&
    (SHIFT_CLOSE_PROTECTED_STATUS as readonly string[]).includes(status)
  );
}

// ---------------------------------------------------------------------------
// Offline POS sync vocabulary — the one explicit adapter
// ---------------------------------------------------------------------------

/**
 * Statuses the offline POS sync queue can hold. NOT order statuses: this is a
 * separate transport state machine, and it was hand-maintaining its own copy of
 * the vocabulary, which is how the two drifted apart.
 */
export const OFFLINE_ORDER_STATUS = [
  'new',
  'acknowledged',
  'preparing',
  'ready',
  'served',
  'completed',
  'rejected',
] as const;

export type OfflineOrderStatus = (typeof OFFLINE_ORDER_STATUS)[number];

/**
 * The single mapping from offline-queue vocabulary to canonical fulfilment
 * status. Every caller that needs to reason about an offline status against the
 * order state machine goes through here — there is no second table.
 *
 *  - `new` is a draft: queued locally, never submitted.
 *  - `acknowledged` means the POS took it; canonically it is `placed`.
 *  - `rejected` is a merchant refusal, i.e. `cancelled`.
 *
 * `preparing`/`ready`/`served`/`completed` are already canonical and map to
 * themselves — kept explicitly so the table reads as a total function over
 * `OFFLINE_ORDER_STATUS` and a new offline status cannot be added without
 * deciding its canonical meaning.
 */
export const OFFLINE_TO_CANONICAL_STATUS: Record<OfflineOrderStatus, OrderStatus> = {
  new: 'draft',
  acknowledged: 'placed',
  preparing: 'preparing',
  ready: 'ready',
  served: 'served',
  completed: 'completed',
  rejected: 'cancelled',
};

export function isOfflineOrderStatus(value: unknown): value is OfflineOrderStatus {
  return (
    typeof value === 'string' &&
    (OFFLINE_ORDER_STATUS as readonly string[]).includes(value)
  );
}

/** Canonical status for an offline-queue status, or null if unknown. */
export function toCanonicalOrderStatus(value: unknown): OrderStatus | null {
  return isOfflineOrderStatus(value) ? OFFLINE_TO_CANONICAL_STATUS[value] : null;
}


// ---------------------------------------------------------------------------
// Ordering Architecture v2 — canonical dimensions (doc v2 §2-8)
// ---------------------------------------------------------------------------

/**
 * Where the order originated (v2 §2.1). Channel is about origin, not
 * fulfillment — two orders with different channels can have the same intent.
 */
export const ORDER_CHANNEL = [
  'ONLINE',
  'QR_TABLE',
  'QR_COUNTER',
  'POS',
  'PHONE',
  'STAFF',
  'INTEGRATION',
] as const;
export type OrderChannel = (typeof ORDER_CHANNEL)[number];

export function isOrderChannel(value: unknown): value is OrderChannel {
  return typeof value === 'string' && (ORDER_CHANNEL as readonly string[]).includes(value);
}

/**
 * What the customer wants to happen with the food (v2 §3). The customer's
 * actual fulfillment intent — not a mode, not a workflow, just the question:
 * "where should this food go?"
 */
export const ORDER_INTENT = ['DINE_IN', 'PICKUP', 'DELIVERY'] as const;
export type OrderIntent = (typeof ORDER_INTENT)[number];

export function isOrderIntent(value: unknown): value is OrderIntent {
  return typeof value === 'string' && (ORDER_INTENT as readonly string[]).includes(value);
}

/**
 * Where the customer is interacting from (v2 §5). Context is spatial, not
 * channel — a QR_TABLE order and a POS order can both have TABLE context.
 */
export const ORDER_CONTEXT = ['TABLE', 'COUNTER', 'REMOTE'] as const;
export type OrderContextV2 = (typeof ORDER_CONTEXT)[number];

export function isOrderContext(value: unknown): value is OrderContextV2 {
  return typeof value === 'string' && (ORDER_CONTEXT as readonly string[]).includes(value);
}

/**
 * When the customer wants the order (v2 §4). PICKUP + ASAP is normal pickup;
 * PICKUP + SCHEDULED is pickup scheduling — not a separate order type.
 */
export const ORDER_TIMING = ['ASAP', 'SCHEDULED'] as const;
export type OrderTiming = (typeof ORDER_TIMING)[number];

export function isOrderTiming(value: unknown): value is OrderTiming {
  return typeof value === 'string' && (ORDER_TIMING as readonly string[]).includes(value);
}

/**
 * Who decides whether the restaurant accepts the order (v2 §6).
 * AUTOMATIC = order proceeds immediately.
 * MANUAL = staff confirms before the order proceeds.
 * SMART = auto-accept normal orders, flag exceptional ones for review.
 */
export const ACCEPTANCE_POLICY = ['AUTOMATIC', 'MANUAL', 'SMART'] as const;
export type AcceptancePolicy = (typeof ACCEPTANCE_POLICY)[number];

export function isAcceptancePolicy(value: unknown): value is AcceptancePolicy {
  return typeof value === 'string' && (ACCEPTANCE_POLICY as readonly string[]).includes(value);
}

/**
 * When payment happens relative to acceptance and preparation (v2 §7).
 */
export const PAYMENT_POLICY = [
  'BEFORE_ACCEPTANCE',
  'AFTER_ACCEPTANCE',
  'AT_COUNTER',
  'AFTER_SERVICE',
] as const;
export type PaymentPolicyV2 = (typeof PAYMENT_POLICY)[number];

export function isPaymentPolicy(value: unknown): value is PaymentPolicyV2 {
  return typeof value === 'string' && (PAYMENT_POLICY as readonly string[]).includes(value);
}

/**
 * How availability is tracked (v2 §8). SIMPLE = on/off toggle.
 * QUANTITY = stock count. INVENTORY = full ingredient tracking.
 */
export const AVAILABILITY_POLICY = ['SIMPLE', 'QUANTITY', 'INVENTORY'] as const;
export type AvailabilityPolicy = (typeof AVAILABILITY_POLICY)[number];

export function isAvailabilityPolicy(value: unknown): value is AvailabilityPolicy {
  return typeof value === 'string' && (AVAILABILITY_POLICY as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Paid detection
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Settlement guard
// ---------------------------------------------------------------------------

/**
 * Statuses an order can never be settled from.
 *
 * NOT the same set as TERMINAL_ORDER_STATUS, and the difference is the point.
 * Fulfilment and payment are orthogonal (see this file's header): a `completed`
 * order can sit `unpaid` on a house account and be settled days later, so
 * `completed` is terminal for fulfilment but still settleable. The three below
 * are orders that ended WITHOUT being fulfilled — there is nothing to collect
 * for, and settling one writes `status='completed'`, `payment_status='paid'`
 * and backfills `accepted_at`/`served_at`, turning a dead order into revenue.
 *
 * Audit ISSUE-01: `updateServiceOrderStatus` classified `paid` as a "payment
 * action" and skipped the transition table entirely for it, so this was
 * reachable from the service console, seat settlement and bulk settle.
 */
export const UNSETTLEABLE_ORDER_STATUS = ['cancelled', 'expired', 'no_show'] as const;
export type UnsettleableOrderStatus = (typeof UNSETTLEABLE_ORDER_STATUS)[number];

/** True when money may still be taken against this order. */
export function canSettleOrder(status: OrderStatus | unknown): boolean {
  return !(
    typeof status === 'string' &&
    (UNSETTLEABLE_ORDER_STATUS as readonly string[]).includes(status)
  );
}

/**
 * Doc 01, defect O3: payment state was smeared across `status` and
 * `payment_status`. This helper papers over that during the transition by
 * reading BOTH columns. STATUS-01 migrates all rows so payment lives only in
 * `payment_status`; once that migration has run in production this helper must
 * be narrowed to read `payment_status` alone (and the legacy `status: 'paid'`
 * branch deleted).
 */
export function isOrderPaid(order: { status?: unknown; payment_status?: unknown }): boolean {
  if (order.payment_status === 'paid') return true;
  return order.status === 'paid';
}
